import { useEffect, useState } from "react";
import { api, IMG } from "@/lib/api";
import { Lock, LogOut, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "soniaClientLibraryToken";

export default function ClientLibrary() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resources, setResources] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoadingRes(true);
    api
      .get("/client/resources", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setResources(res.data.resources || []);
        setError(null);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
        } else {
          setError(e?.response?.data?.detail || e.message);
        }
      })
      .finally(() => setLoadingRes(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await api.post("/client/auth", { password });
      if (res.data.success && res.data.token) {
        sessionStorage.setItem(TOKEN_KEY, res.data.token);
        setToken(res.data.token);
        setPassword("");
        toast.success("Welcome to the library");
      } else {
        toast.error(res.data.message || "Incorrect password");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setResources([]);
    toast.message("Logged out");
  };

  if (!token) {
    return (
      <div className="relative min-h-[80vh] flex items-center justify-center" data-testid="library-locked">
        <img src={IMG.interior2} alt="Library" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-[#1A1D20]/55" />
        <div className="relative bg-paper border border-soft p-10 md:p-14 max-w-md w-[90%] mx-auto">
          <Lock size={28} className="text-sand" strokeWidth={1.25} />
          <p className="overline mt-6">Members only</p>
          <h2 className="mt-3 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
            Client Resource Library
          </h2>
          <p className="mt-4 text-soft text-sm">
            Enter the password Sonia shared with you to access checklists, vendor lists, market
            reports, and more.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="library-form">
            <div>
              <label className="overline">Password</label>
              <input
                type="password"
                className="input-editorial mt-2"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="library-password-input"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !password}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="library-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" /> Unlocking…
                </>
              ) : (
                "Enter library"
              )}
            </button>
          </form>
          <p className="mt-6 text-xs text-soft">
            Don't have the password? <a href="/contact" className="underline hover:text-ocean">Get in touch.</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="library-unlocked">
      <section className="container-edge pt-16 pb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="overline">Client Library</p>
            <h1 className="mt-6 text-5xl md:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Welcome back.
            </h1>
            <p className="mt-4 text-soft max-w-2xl">
              Your private resource collection. Bookmark this page — it grows over time.
            </p>
          </div>
          <button
            onClick={onLogout}
            className="btn-outline flex items-center"
            data-testid="library-logout-btn"
          >
            <LogOut size={14} className="mr-2" /> Log out
          </button>
        </div>
      </section>

      <section className="section pt-12" data-testid="library-resources">
        {loadingRes && (
          <div className="flex items-center gap-3 text-soft">
            <Loader2 size={18} className="animate-spin" /> Loading resources…
          </div>
        )}
        {error && <p className="text-red-500">{error}</p>}
        {!loadingRes && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {resources.map((r) => (
              <div key={r.id} className="editorial-card p-8 flex flex-col" data-testid={`resource-${r.id}`}>
                <FileText size={24} className="text-sand" strokeWidth={1.25} />
                <p className="overline mt-6">{r.category} · {r.type}</p>
                <h3 className="mt-3 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>{r.title}</h3>
                <p className="mt-3 text-soft text-sm flex-1">{r.description}</p>
                <a
                  href={r.url}
                  className="mt-6 inline-flex items-center text-sm text-ocean hover:text-sand transition-colors self-start"
                  data-testid={`resource-${r.id}-link`}
                >
                  Open →
                </a>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
